import type { ThemeClasses, RoutePoint } from "../types";
import type { DenseRouteCoordinate } from "../../../../../../store/admin/routesStore";
import MapPicker from "../../../../../../components/base/MapPicker";

export interface RouteMapPreviewProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  isEdit: boolean;
  routePoints: RoutePoint[];
  routeDistance: number;
  estimatedTime: string;
  firstPointRadius: number;
  lastPointRadius: number;
  corridorBuffer: number;
  storedAxis?: DenseRouteCoordinate[];
  onPointsChange: (newPoints: { lat: number; lng: number }[]) => void;
  onRouteCalculated: (distance: number) => void;
  onAxisCalculated?: (path: { lat: number; lng: number }[]) => void;
  onFirstPointRadiusChange?: (radius: number) => void;
  onLastPointRadiusChange?: (radius: number) => void;
  readOnly?: boolean;
}

export function RouteMapPreview({
  themeClasses,
  isDarkMode,
  isEdit,
  routePoints,
  routeDistance,
  estimatedTime,
  firstPointRadius,
  lastPointRadius,
  corridorBuffer,
  storedAxis,
  onPointsChange,
  onRouteCalculated,
  onAxisCalculated,
  onFirstPointRadiusChange,
  onLastPointRadiusChange,
  readOnly = false,
}: RouteMapPreviewProps) {
  const coordinates = routePoints.map((p) => ({
    lat: Number(p.latitude),
    lng: Number(p.longitude),
  }));

  const axis =
    readOnly && Array.isArray(storedAxis) && storedAxis.length >= 2
      ? storedAxis.map((p) => ({
          lat: Number(p.latitude),
          lng: Number(p.longitude),
        }))
      : undefined;

  const originPoint = routePoints[0];
  const destPoint = routePoints[routePoints.length - 1];

  return (
    <div
      className={`w-full h-full flex flex-col gap-3 ${
        isDarkMode ? "bg-dark-card" : "bg-gray-50"
      } p-4 2xl:p-6`}
    >
      {/* Legend */}
      <div className="flex items-center gap-3 flex-wrap">
        <span
          className={`text-sm font-semibold ${themeClasses.text}`}
        >
          Vista previa de la ruta
        </span>
        <div className="flex items-center gap-2.5 ml-auto flex-wrap">
          <span className={`flex items-center gap-1 text-xs ${themeClasses.textSecondary}`}>
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
            Origen
          </span>
          {routePoints.length > 2 && (
            <span className={`flex items-center gap-1 text-xs ${themeClasses.textSecondary}`}>
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" />
              Vía
            </span>
          )}
          <span className={`flex items-center gap-1 text-xs ${themeClasses.textSecondary}`}>
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />
            Destino
          </span>
          {corridorBuffer > 0 && (
            <span
              className={`flex items-center gap-1 text-xs ${themeClasses.textSecondary}`}
              title="Salir de esta franja es lo que dispara la alerta de desvío"
            >
              <span className="w-3.5 h-2.5 rounded-sm inline-block border border-fuchsia-600 bg-fuchsia-200/60" />
              Corredor
            </span>
          )}
          {(firstPointRadius > 0 || lastPointRadius > 0) && (
            <span
              className={`flex items-center gap-1 text-xs ${themeClasses.textSecondary}`}
              title="Zonas que marcan el inicio y el fin del viaje"
            >
              <span className="w-2.5 h-2.5 rounded-full inline-block border border-emerald-500 bg-emerald-500/20" />
              Zonas
            </span>
          )}
        </div>
      </div>

      <MapPicker
        shape="linear"
        type="route"
        coordinates={coordinates}
        onPointsChange={readOnly ? undefined : onPointsChange}
        onRouteCalculated={readOnly ? undefined : onRouteCalculated}
        estimatedTime={estimatedTime}
        polygonArea={0}
        routeDistance={routeDistance}
        lat={originPoint?.latitude || 19.4326}
        lng={originPoint?.longitude || -99.1332}
        radius={0}
        isEdit={readOnly ? false : isEdit}
        firstPointRadius={firstPointRadius}
        lastPointRadius={lastPointRadius}
        corridorBuffer={corridorBuffer}
        storedAxis={axis}
        onAxisCalculated={readOnly ? undefined : onAxisCalculated}
        onFirstPointRadiusChange={readOnly ? undefined : onFirstPointRadiusChange}
        onLastPointRadiusChange={readOnly ? undefined : onLastPointRadiusChange}
      />

      {/* Route summary */}
      {!readOnly && originPoint && destPoint && (
        <div
          className={`grid grid-cols-1 sm:grid-cols-4 gap-3 text-xs rounded-lg p-3 border ${themeClasses.cardBorder} ${
            isDarkMode ? "bg-dark-surface" : "bg-white"
          }`}
        >
          <div>
            <span className="text-gray-400 block">Distancia</span>
            <span className={`font-semibold ${themeClasses.text}`}>
              {routeDistance > 0 ? `${routeDistance.toFixed(1)} km` : "—"}
            </span>
          </div>
          <div>
            <span className="text-gray-400 block">Tiempo estimado</span>
            <span className={`font-semibold ${themeClasses.text}`}>
              {estimatedTime || "—"}
            </span>
          </div>
          <div>
            <span className="text-gray-400 block">Puntos</span>
            <span className={`font-semibold ${themeClasses.text}`}>
              {routePoints.length}{" "}
              <span className="font-normal text-gray-400">
                ({routePoints.length - 2} int.)
              </span>
            </span>
          </div>
          <div>
            <span className="text-gray-400 block">Corredor</span>
            <span className={`font-semibold ${themeClasses.text}`}>
              {corridorBuffer > 0 ? `${corridorBuffer * 2} m` : "—"}
            </span>
          </div>
        </div>
      )}

      {!readOnly && (
        <p className="text-[11px] text-gray-400 leading-relaxed">
          Arrastra los marcadores para ajustar la ruta. Los cambios en la tabla de puntos también se reflejan aquí.
        </p>
      )}
    </div>
  );
}
